import Switch from "@components/Switch/Switch";
import { Checkbox } from "@components/ui/checkbox";
import { ITeamMember } from "@type/hiringStaff.type";
import { useEffect, useState } from "react";
import { HiringCompanyType } from "@utils/constants";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";

type HiringTeamMembersProps = {
  jobRequirementTeamMembers: ITeamMember[];
  selectedHiringMembers: ITeamMember[];
  handleSelectOrDeselectMember: (member: ITeamMember) => void;
  sendPollSwitch: boolean;
  setSendPollSwitch: React.Dispatch<React.SetStateAction<boolean>>;
};

const HiringTeamMembers = (props: HiringTeamMembersProps) => {
  const {
    jobRequirementTeamMembers,
    selectedHiringMembers,
    handleSelectOrDeselectMember,
    sendPollSwitch,
    setSendPollSwitch,
  } = props;
  const [teamMemberList, setTeamMemberList] = useState<ITeamMember[]>([]);
  const currentUser = useSelector((state: RootState) => state.auth?.user);
  const currentUserId = currentUser?._id || currentUser?.userId;

  const roleLabelMap: Record<number, string> = {
    [HiringCompanyType.SUPER_ADMIN]: "Super Admin",
    [HiringCompanyType.ADMIN]: "Admin",
    [HiringCompanyType.MANAGER]: "Hiring Manager",
    [HiringCompanyType.TEAM_MEMBER]: "Hiring Team Member",
  };

  useEffect(() => {
    const onlyHiringTeamMembers = (jobRequirementTeamMembers || []).filter(
      (member) =>
        [
          HiringCompanyType.MANAGER,
          HiringCompanyType.TEAM_MEMBER,
        ].includes(Number(member?.hiringCompanyType))
    );

    const filteredMembers = onlyHiringTeamMembers.filter((member) => {
      if (!currentUserId) return true;
      return (
        String(member?.userId) !== String(currentUserId) &&
        String(member?._id) !== String(currentUserId)
      );
    });

    setTeamMemberList(filteredMembers);
  }, [jobRequirementTeamMembers, currentUserId]);

  return (
    <div className="flex flex-col">
      {/* Hiring member add switch */}
      <div className="flex items-center justify-between mb-8">
        <span className="font-medium text-xs text-charcoal">
          Do you want to first send poll to your hiring team members ?
        </span>
        <Switch
          isChecked={sendPollSwitch}
          onToggle={(value) => setSendPollSwitch(value?.target?.checked)}
        />
      </div>
      {/* Member listing */}
      {sendPollSwitch ? (
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-xs text-charcoal">
            Select hiring team members for availability poll
          </span>
          {teamMemberList?.length ? (
            <div className="flex flex-col border py-3 px-5 rounded-md gap-2">
              {teamMemberList?.map((member) => {
                const isSelected = !!selectedHiringMembers?.find(
                  (item) => item?._id === member?._id
                );
                return (
                  <div key={member?._id} className="flex items-center gap-4">
                    <Checkbox
                      checked={isSelected}
                      onClick={() => handleSelectOrDeselectMember(member)}
                      className="w-5 h-5"
                    />
                    <div className="flex items-center gap-3.5">
                      {member?.profilePic ? (
                        <img
                          src={member?.profilePic}
                          className="w-6 h-6 rounded-md"
                        />
                      ) : (
                        <div className="flex justify-center items-center w-6 h-6 rounded-full bg-gray-200 text-xs font-medium text-charcoal">
                          {member?.firstName?.charAt(0)}
                        </div>
                      )}
                      <span className="text-xs font-medium text-light-black">
                        {member?.firstName} {member?.lastName}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({roleLabelMap[Number(member?.hiringCompanyType)] || "Member"})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-2 border rounded-md p-3">
              <span className="font-medium text-xs text-light-black">
                No hiring team members are assigned to this job requirement.
              </span>
              <span className="text-xs text-gray-500">
                Add members in the job requirement first, then they will appear here.
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default HiringTeamMembers;
